import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { sectionPath } from '../siteRoutes';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { DateSelectionBar } from './rooms/DateSelectionBar';
import { BestRateBanner } from './rooms/BestRateBanner';
import { RoomCard } from './rooms/RoomCard';
import { dealsForRoom, useDealCode } from './rooms/dealCode';
import { BookingSummaryModal } from './rooms/BookingSummaryModal';
import { getSavingsVsBcom } from './rooms/pricing';
import {
  trackRoomsPageView,
  trackDatesSelected,
  trackAvailabilitySeen,
  trackBookModalOpened,
  trackBookConfirmed,
} from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Rooms = ({ limit }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rooms: allRooms, hotelInfo } = useHotel();
  const { t } = useLanguage();
  const mockRooms = limit ? allRooms.slice(0, limit) : allRooms;
  const [imgIdx, setImgIdx] = useState({});
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [dealCode] = useDealCode();
  const [deals, setDeals] = useState([]);

  // Deal ribbons: for the chosen dates when set, otherwise every deal still bookable today (secret ones only with the link code).
  useEffect(() => {
    let alive = true;
    const q = checkIn && checkOut ? `check_in=${checkIn}&check_out=${checkOut}` : '';
    fetch(`${API_URL}/api/public/rate-offers?${q}${dealCode ? `&code=${encodeURIComponent(dealCode)}` : ''}`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive && d) setDeals(d.promotions || []); }).catch(() => {});
    return () => { alive = false; };
  }, [checkIn, checkOut, dealCode]);

  // Funnel step 1: rooms grid rendered
  useEffect(() => { trackRoomsPageView(); }, []);

  // Funnel step 2: fire once dates are picked
  useEffect(() => {
    if (checkIn && checkOut) trackDatesSelected();
  }, [checkIn, checkOut]);

  const today = new Date().toISOString().split('T')[0];
  const minCheckout = checkIn
    ? new Date(new Date(checkIn).getTime() + 86400000).toISOString().split('T')[0]
    : today;

  useEffect(() => {
    if (!checkIn || !checkOut) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/beds24/availability?check_in=${checkIn}&check_out=${checkOut}&adults=${adults}&children=${children}`
        );
        if (!r.ok) { if (!cancelled) setAvailability(null); return; }
        const data = await r.json();
        if (!cancelled) {
          setAvailability(data.success ? data : null);
          if (data.success) trackAvailabilitySeen();
        }
      } catch (e) {
        console.error('Availability error:', e);
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [checkIn, checkOut, adults, children]);

  // Persist booking data so /room/:id can pick it up.
  useEffect(() => {
    if (checkIn && checkOut && availability) {
      localStorage.setItem('bookingData', JSON.stringify({
        checkIn, checkOut, adults, children,
        numNights: availability.num_nights,
        timestamp: Date.now(),
      }));
    }
  }, [checkIn, checkOut, adults, children, availability]);

  const getRoomPricing = (roomId) =>
    availability?.rooms?.find((r) => r.local_room_id === roomId) || null;

  const savingsRange = useMemo(() => {
    if (!availability?.rooms) return null;
    const totals = availability.rooms
      .filter((r) => r.available && r.pricing?.total_price != null)
      .map((r) => getSavingsVsBcom(r.pricing.total_price));
    if (totals.length === 0) return null;
    return { min: Math.min(...totals), max: Math.max(...totals), count: totals.length };
  }, [availability]);

  const openBookingModal = (room, pricing) => {
    setSelectedRoom({ ...room, pricing });
    setShowBookingModal(true);
    document.body.style.overflow = 'hidden';
    trackBookModalOpened(room?.id);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedRoom(null);
    document.body.style.overflow = 'auto';
  };

  const handleCompleteBooking = () => {
    trackBookConfirmed(selectedRoom?.id);
    let url = `/booking?propid=${hotelInfo.beds24Id}`;
    if (checkIn) url += `&checkin=${checkIn}`;
    if (checkOut) url += `&checkout=${checkOut}`;
    url += `&adults=${adults}`;
    url += `&children=${children}`;
    if (selectedRoom?.beds24Id) url += `&roomId=${selectedRoom.beds24Id}`;
    closeBookingModal();
    navigate(url);
  };

  return (
    <>
      <section id="rooms" className="py-20 bg-gray-50" data-testid="rooms-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider">{t('rooms.subtitle')}</p>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900" data-testid="rooms-heading">{t('rooms.title')}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t('rooms.description')}</p>
          </div>

          <DateSelectionBar
            checkIn={checkIn}
            checkOut={checkOut}
            adults={adults}
            children={children}
            loading={loading}
            availability={availability}
            today={today}
            minCheckout={minCheckout}
            onCheckInChange={setCheckIn}
            onCheckOutChange={setCheckOut}
            onAdultsChange={setAdults}
            onChildrenChange={setChildren}
          />

          <BestRateBanner savingsRange={savingsRange} />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockRooms.map((room) => (
              <RoomCard
                key={room.id}
                room={room}
                pricing={getRoomPricing(room.id)}
                availability={availability}
                imgIdx={imgIdx[room.id] || 0}
                onImgChange={(id, i) => setImgIdx((p) => ({ ...p, [id]: i }))}
                onBook={openBookingModal}
                deals={dealsForRoom(deals, room.beds24Id)}
              />
            ))}
          </div>
          {limit && allRooms.length > limit && (
            <div className="mt-10 text-center">
              <Link to={sectionPath('rooms', location.pathname)} className="inline-flex items-center gap-2 rounded-full border-2 border-orange-500 text-orange-600 font-semibold px-6 py-3 hover:bg-orange-500 hover:text-white transition-colors" data-testid="rooms-view-all">
                {t('nav.rooms')} · {allRooms.length} →
              </Link>
            </div>
          )}
        </div>
      </section>

      <BookingSummaryModal
        open={showBookingModal}
        onClose={closeBookingModal}
        onConfirm={handleCompleteBooking}
        room={selectedRoom}
        bookingSummary={{
          checkIn, checkOut, adults, children,
          numNights: availability?.num_nights,
        }}
      />
    </>
  );
};

export default Rooms;
